import React, { useRef, useState } from "react"
import { gsap, useGSAP } from "@repo/utils/gsap"
import { CURSOR_TYPES, ICONS } from "@repo/utils/constants"
import Image from "../../common/Image"
import WebGLText from "../../WebGL/Text/Text"
import { isFunction } from "@repo/utils"
import useVisible3D from "../../../hooks/useVisible3D"
import { BreakWrapper } from "./Components"
import CircleIcon from "../Icon/CircleIcon"
import styled from "styled-components"
import { HOVER_VARIANTS } from "./Classic/Row"
import { ANIMATION as TXT_ANIMATION } from "./utils"
import { useTextWithBreaksContext } from "./index"
import { useIsDesktop } from "../../../providers/DeviceProvider"
import { useNavigation } from "../../../providers/NavigationProvider"

const Icon = styled(CircleIcon)`
	--width-scale: 1;
	--image: -100;
	margin-right: calc(var(--column-gap) - 2px);
	position: relative;
	width: calc(var(--size) * var(--width-scale)) !important;
	height: var(--size);
	transition:
		box-shadow var(--color-transition-duration) linear,
		border var(--color-transition-duration) linear;
	box-shadow: 0 0 10px -2px transparent;

	&.hover {
		border: solid var(--border-width) transparent;
	}

	&.disabled {
		cursor: auto;
	}

	&.clicked {
		box-shadow: 0px 0px 10px -2px #57678b;
	}

	@media (min-width: ${({ theme }) => theme?.breakpoints?.tablet || 768}px) {
		margin-right: calc(var(--column-gap) * 0.6 - 3px);

		&.hover {
			svg {
				path {
					transform: translateX(-10%);
				}
			}
		}
	}
`

export function getDelay(index) {
	return index / 10
}

const TitleBreak = ({
	icon = ICONS.WAVE,
	image,
	hover = HOVER_VARIANTS.NONE,
	variant,
	color,
	className = "",
	onClick,
	onPointerEnter,
	onPointerLeave,
	webGL = false,
	left = false,
	imageLoadPriority = false,
	noCursorHover,
	uIndex = 0,
	isStory = false,
	children,
}) => {
	const IconRef = useRef()
	const TextRef = useRef()
	const visible3D = useVisible3D(webGL)
	const isDesktop = useIsDesktop()
	const { animation } = useTextWithBreaksContext()
	const transitionCompleted = useNavigation((state) => state.isTransitionCompleted)
	const src = image?.url
	const alt = image?.alt || "image"
	const [init, setInit] = useState(false)
	const dataCursor = noCursorHover ? undefined : left ? CURSOR_TYPES.CHIP_LEFT : CURSOR_TYPES.CHIP

	useGSAP(
		() => {
			if (!init || isStory) return
			const width = isDesktop && hover === HOVER_VARIANTS.HOVER ? 2 : 1
			const image = hover === HOVER_VARIANTS.HOVER ? "0" : "-100"
			gsap.to(IconRef.current, {
				duration: 0.5,
				ease: "expo.inOut",
				"--width-scale": width,
				"--image": image,
			})
		},
		{
			scope: IconRef.current,
			dependencies: [isDesktop, hover, init, isStory],
		},
	)

	useGSAP(
		() => {
			if (isStory) return
			if (animation === TXT_ANIMATION.INIT_SHOW) {
				gsap.set(IconRef.current, { scale: 0, transformOrigin: "center" })
				// gsap.set(TextRef.current, { x: visible3D ? 0 : -33 })
				if (!transitionCompleted) return
				gsap.to(IconRef.current, {
					scale: 1,
					delay: getDelay(uIndex) * (visible3D ? 1 : 1.5),
					onComplete: () => {
						setInit(true)
					},
				})
			} else {
				setInit(true)
			}
		},
		{
			dependencies: [visible3D, transitionCompleted, isStory],
			revertOnUpdate: true,
		},
	)

	const handleOnPointerEnter = () => {
		if (!isDesktop || !isFunction(onPointerEnter)) return
		onPointerEnter()
	}
	const handleOnPointerLeave = () => {
		if (!isDesktop || !isFunction(onPointerLeave)) return
		onPointerLeave()
	}
	const handleOnClick = () => {
		if (!isFunction(onClick)) return
		onClick()
	}

	return (
		<BreakWrapper className={`${className} break-wrapper`}>
			{!isStory && (
				<Icon
					ref={IconRef}
					name={icon}
					className={`${hover}  ${!webGL || !visible3D ? "responsive" : ""} ${noCursorHover ? "disabled" : ""} wave `}
					data-cursor={dataCursor}
					onPointerEnter={handleOnPointerEnter}
					onPointerLeave={handleOnPointerLeave}
					onClick={handleOnClick}
					color={color}
				>
					<Image
						src={src}
						alt={alt}
						style={{
							position: "absolute",
							pointerEvents: "none",
						}}
						sizes="(max-width: 1023px) 2rem, 8vw"
						priority={imageLoadPriority}
					/>
				</Icon>
			)}
			<WebGLText
				ref={TextRef}
				hover={hover === "hover" || hover === "move"}
				data-cursor={webGL ? CURSOR_TYPES.TEXT3D : null}
				variant={variant}
				color={color}
				show3D={webGL}
				onClick={handleOnClick}
				className={`${isStory ? "story" : ""}`}
				// onClick={() => {
				//     if(isDesktop || !isFunction(onClick)) return
				//     onClick()
				// }}
				uIndex={uIndex}
			>
				{children}
			</WebGLText>
		</BreakWrapper>
	)
}

export default TitleBreak
